<template>
  <div class="infocar-grafiche px-4 pb-2 mt-3">
    <v-card
      v-if="showVehicleSummary && vehicleSelectionSummary"
      variant="outlined"
      class="mb-4 grafiche-vehicle-summary"
    >
      <v-card-text class="py-3">
        <div class="text-caption text-medium-emphasis mb-2">
          {{ $t("vehicle_entry.grafiche_selected_vehicle") }}
        </div>
        <v-row dense>
          <v-col cols="12" sm="4">
            <div class="text-caption text-medium-emphasis">
              {{ $t("parts.advanced_search.brand") }}
            </div>
            <div class="text-body-1 font-weight-medium">
              {{ vehicleSelectionSummary.brand }}
            </div>
          </v-col>
          <v-col cols="12" sm="4">
            <div class="text-caption text-medium-emphasis">
              {{ $t("parts.advanced_search.model") }}
            </div>
            <div class="text-body-1 font-weight-medium">
              {{ vehicleSelectionSummary.model }}
            </div>
          </v-col>
          <v-col cols="12" sm="4">
            <div class="text-caption text-medium-emphasis">
              {{ $t("parts.advanced_search.version") }}
            </div>
            <div class="text-body-1 font-weight-medium">
              {{ vehicleSelectionSummary.version }}
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-divider
      v-if="showVehicleSummary && vehicleSelectionSummary"
      class="my-6"
    />

    <div class="text-subtitle-2 font-weight-medium mb-2">
      {{ $t("vehicle_entry.grafiche_quick_browse") }}
    </div>
    <v-list
      class="grafiche-quick-categories pa-0 border rounded"
      density="comfortable"
    >
      <v-list-item
        v-for="(cat, quickIdx) in sortedCategories"
        :key="'quick-' + cat.categoryCode"
        :class="['grafiche-quick-row', stripeClass(quickIdx)]"
        @click="openCategoryModal(cat.categoryCode)"
      >
        <template v-if="showCategoryBulkSelect" #prepend>
          <v-checkbox
            :model-value="categoryAllSelected(cat.categoryCode)"
            :disabled="readonly"
            color="red-darken-1"
            hide-details
            density="compact"
            class="mt-0 grafiche-quick-category-check flex-shrink-0"
            @click.stop
            @update:model-value="
              (v) => applyCategorySelectAll(cat.categoryCode, v)
            "
          />
        </template>
        <template #append>
          <v-icon color="grey-darken-1"> mdi-chevron-right </v-icon>
        </template>
        <v-list-item-title class="text-wrap">
          {{ categoryLabel(cat) }}
        </v-list-item-title>
      </v-list-item>
    </v-list>

    <v-dialog
      v-model="categoryModalOpen"
      max-width="900"
      scrollable
      @after-leave="onCategoryModalClosed"
    >
      <v-card>
        <v-card-title class="redColor d-flex align-center text-white">
          <span class="text-h6 text-wrap pr-2">
            {{ modalCategoryTitle }}
          </span>
          <v-spacer />
          <v-btn
            variant="outlined"
            icon
            color="white"
            @click="closeCategoryModal"
          >
            <v-icon> mdi-close </v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-0">
          <v-progress-linear
            v-if="categoryModalLoading && catalogFromPartCategoryGroupsApi"
            indeterminate
            color="red-darken-3"
          />
          <div
            v-if="
              showCategoryBulkSelect &&
              modalCategoryCode &&
              !categoryModalLoading
            "
            class="px-3 pt-3"
          >
            <v-checkbox
              :model-value="categoryAllSelected(modalCategoryCode)"
              :disabled="readonly"
              color="red-darken-1"
              hide-details
              density="compact"
              class="mt-0"
              :label="$t('vehicle_entry.grafiche_modal_all_in_category')"
              @update:model-value="
                (v) => applyCategorySelectAll(modalCategoryCode, v)
              "
            />
          </div>
          <v-divider
            v-if="
              showCategoryBulkSelect &&
              modalCategoryCode &&
              !categoryModalLoading
            "
          />
          <template v-if="!categoryModalLoading">
            <div
              v-for="(item, idx) in modalCategoryLines"
              :key="item._id + '-modal'"
              :class="['grafiche-inner-row px-3 py-2', stripeClass(idx)]"
            >
              <v-row align="center" class="ma-0">
                <v-col cols="auto" class="py-1">
                  <v-checkbox
                    v-model="item.selected"
                    :disabled="readonly"
                    hide-details
                    color="red-darken-1"
                    density="compact"
                    class="mt-0"
                  />
                </v-col>
                <v-col cols="12" sm="5" md="4" class="py-1">
                  <div class="pt-1">
                    {{ lineTitle(item) }}
                  </div>
                </v-col>
                <v-col cols="12" sm class="py-1">
                  <v-text-field
                    v-model="item.notes"
                    :readonly="readonly"
                    :label="$t('vehicle_entry.notes')"
                    density="compact"
                    hide-details
                    color="red-darken-1"
                  />
                </v-col>
              </v-row>
            </div>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="grey-darken-3"
            variant="outlined"
            @click="closeCategoryModal"
          >
            {{ $t("vehicle_entry.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-textarea
      v-if="showGlobalComment"
      v-model="globalComment"
      class="mt-4"
      :readonly="readonly"
      :label="$t('vehicle_entry.comment')"
      color="red-darken-1"
      rows="2"
      auto-grow
      hide-details
    />
  </div>
</template>

<script>
/* eslint-disable vue/no-mutating-props -- vehicle is the parent stepper draft; nested parts follow VehicleEntry patterns */
import {
  DEFAULT_GRAFICHE_CATEGORIES,
  buildMockTitlesForCategory,
} from "@/data/infocarGraficheCategories.js";
import {
  GRAFICHE_CATALOG_UPDATED_EVENT,
  hasStoredGraficheCatalog,
  loadGraficheCatalog,
  mergeLinesWithVehicleDefaults,
} from "@/services/graficheCatalogAdminStore.js";
import {
  fetchPartCategoryGroupsCategoriesOnly,
  fetchPartsTemplatesForCategory,
} from "@/services/partCategoryGroupsCatalog.js";
import {
  findCatalogCategoryForSnapshotBlock,
  mergeVehicleSlotsFromPartsByCategory,
  serializePartsByCategory,
} from "@/services/partsByCategorySync.js";

function cloneLinesMap(obj) {
  return JSON.parse(JSON.stringify(obj || {}));
}

/** Optional: fetch catalog from CDN or backend without redeploy. Return JSON array shaped like DEFAULT_GRAFICHE_CATEGORIES. */
async function fetchGraficheCatalogFromUrl(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Grafiche catalog HTTP ${res.status}`);
  const data = await res.json();
  return Array.isArray(data) ? data : data.categories;
}

export default {
  name: "InfocarGraficheAccordion",

  props: {
    vehicle: {
      type: Object,
      required: true,
    },
    /**
     * When set (e.g. VUE_APP_GRAFICHE_CATALOG_URL), loaded on mount; falls back to bundled defaults on failure.
     */
    catalogUrl: {
      type: String,
      default: "",
    },
    /** View-only dialogs (details): no Grafiche edits. */
    readonly: {
      type: Boolean,
      default: false,
    },
    /** Hide brand/model/version card (e.g. details/edit dialogs already show it). */
    showVehicleSummary: {
      type: Boolean,
      default: true,
    },
    /**
     * New vehicle flow: prefetch every category's parts and initialise all titles as selected.
     * Keep false for details/edit dialogs to avoid hammering `/parts`.
     */
    eagerLoadCategoryParts: {
      type: Boolean,
      default: false,
    },
    /** Select-all checkboxes on the category list and in the category modal (part line checkboxes stay visible). */
    showCategoryBulkSelect: {
      type: Boolean,
      default: false,
    },
    /** Global Grafiche comment field (`vehicle.parts.comment`). */
    showGlobalComment: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    categories: [],
    loadError: false,
    /** Backend part-category-groups: only fetch /parts when user opens/interacts with a category. */
    catalogFromPartCategoryGroupsApi: false,
    categoryModalLoading: false,
    /** @type {Record<string, Promise<void>>} dedupe parallel loads */
    categoryPartsInflight: {},
    /** Line templates from admin localStorage (categoryCode -> rows without selected/notes). */
    lineTemplatesByCategory: {},
    categoryModalOpen: false,
    modalCategoryCode: null,
    ensuringSlots: false,
  }),

  computed: {
    sortedCategories() {
      return [...this.categories].sort(
        (a, b) => (a.sortOrder || 0) - (b.sortOrder || 0),
      );
    },

    globalComment: {
      get() {
        if (!this.vehicle.parts || this.vehicle.parts.comment == null)
          return "";
        return this.vehicle.parts.comment;
      },
      set(val) {
        if (this.readonly) return;
        if (!this.vehicle.parts) this.vehicle.parts = {};
        this.vehicle.parts.comment = val;
      },
    },

    vehicleSelectionSummary() {
      const v = this.vehicle;
      if (!v) return null;

      const fromDetails = v.details?.brand ? v.details : null;
      const detailOrTop =
        fromDetails ||
        (v.brand && v.model && v.version
          ? {
              brand: v.brand,
              model: v.model,
              version: v.version,
            }
          : null);

      if (!detailOrTop?.brand) return null;

      const brand = detailOrTop.brand;
      const brandText =
        (typeof brand.title === "string" && brand.title) ||
        brand.title?.[this.lang()] ||
        brand.title?.it ||
        "";

      const model = detailOrTop.model;
      const modelText = model?.fullDescription || model?.description || "";

      const ver = detailOrTop.version;
      const versionText = ver?.fullDescription || ver?.description || "";

      return {
        brand: brandText || "—",
        model: modelText || "—",
        version: versionText || "—",
      };
    },

    modalCategory() {
      if (!this.modalCategoryCode) return null;
      return (
        this.sortedCategories.find(
          (c) => c.categoryCode === this.modalCategoryCode,
        ) || null
      );
    },

    modalCategoryTitle() {
      return this.modalCategory ? this.categoryLabel(this.modalCategory) : "";
    },

    /** Same array ref as accordion — edits stay in sync. */
    modalCategoryLines() {
      if (!this.modalCategoryCode) return [];
      return this.itemsForCategory(this.modalCategoryCode);
    },
  },

  watch: {
    vehicle: {
      handler() {
        this.ensureSlots();
      },
      deep: true,
      flush: "post",
    },

    sortedCategories: {
      handler() {
        this.ensureSlots();
      },
      deep: false,
    },
  },

  async mounted() {
    await this.loadCategories();
    window.addEventListener(
      GRAFICHE_CATALOG_UPDATED_EVENT,
      this.onGraficheCatalogUpdated,
    );
    await this.hydrateFromVehiclePartsByCategoryIfNeeded();
    await this.maybeEagerPrefetchAllCategoryParts();
    this.ensureSlots();
  },

  beforeUnmount() {
    window.removeEventListener(
      GRAFICHE_CATALOG_UPDATED_EVENT,
      this.onGraficheCatalogUpdated,
    );
  },

  methods: {
    onGraficheCatalogUpdated() {
      this.loadCategories().then(async () => {
        await this.hydrateFromVehiclePartsByCategoryIfNeeded();
        await this.maybeEagerPrefetchAllCategoryParts();
        this.ensureSlots();
      });
    },

    /**
     * Rebuild Grafiche slots from persisted `vehicle.partsByCategory` (details / edit).
     */
    async hydrateFromVehiclePartsByCategoryIfNeeded() {
      const snap = this.vehicle?.partsByCategory;
      if (
        !snap ||
        typeof snap !== "object" ||
        !Array.isArray(snap.categories) ||
        snap.categories.length === 0
      ) {
        return;
      }

      mergeVehicleSlotsFromPartsByCategory({
        vehicle: this.vehicle,
        snapshot: snap,
        catalogCategories: this.sortedCategories,
        lineTemplatesByCategory: this.lineTemplatesByCategory,
      });
    },

    /** Parent save payload: aligns with Grafiche checklist + CAD `parts.comment`. */
    getPartsByCategorySnapshot() {
      return serializePartsByCategory(
        this.vehicle?.parts,
        this.sortedCategories,
      );
    },

    /**
     * Prefetch every category's lines and run `ensureSlots` so `vehicle.parts` and
     * `getPartsByCategorySnapshot()` cover the full catalog—not only categories opened in the UI.
     */
    async prepareSlotsForPersist() {
      if (this.readonly || !this.vehicle) return;
      if (this.catalogFromPartCategoryGroupsApi) {
        await Promise.all(
          this.sortedCategories.map((c) =>
            this.prefetchCategoryPartsIfNeeded(c.categoryCode),
          ),
        );
      }
      this.ensureSlots();
    },

    async maybeEagerPrefetchAllCategoryParts() {
      if (
        !this.eagerLoadCategoryParts ||
        !this.catalogFromPartCategoryGroupsApi ||
        this.readonly
      ) {
        return;
      }
      await Promise.all(
        this.categories.map((c) =>
          this.prefetchCategoryPartsIfNeeded(c.categoryCode),
        ),
      );
    },

    /** When filling a category slot from the catalog, default every line selected (new vehicle only). */
    applyAllSelectedToSlot(key) {
      if (
        !this.eagerLoadCategoryParts ||
        this.readonly ||
        !this.vehicle?.parts
      ) {
        return;
      }
      const lines = this.vehicle.parts[key];
      if (!Array.isArray(lines)) return;
      for (const line of lines) {
        if (line && typeof line === "object") line.selected = true;
      }
    },

    async prefetchCategoryPartsIfNeeded(categoryCode) {
      if (!this.catalogFromPartCategoryGroupsApi) return;
      if (this.lineTemplatesByCategory[categoryCode] !== undefined) return;

      let p = this.categoryPartsInflight[categoryCode];
      if (!p) {
        p = fetchPartsTemplatesForCategory(categoryCode)
          .then((rows) => {
            this.lineTemplatesByCategory[categoryCode] = rows;
            this.ensureSlots();
          })
          .catch(() => {
            this.lineTemplatesByCategory[categoryCode] = [];
            this.ensureSlots();
          })
          .finally(() => {
            delete this.categoryPartsInflight[categoryCode];
          });
        this.categoryPartsInflight[categoryCode] = p;
      }
      await p;
    },

    async openCategoryModal(categoryCode) {
      this.modalCategoryCode = categoryCode;
      this.categoryModalOpen = true;
      if (this.catalogFromPartCategoryGroupsApi) {
        this.categoryModalLoading = true;
        try {
          await this.prefetchCategoryPartsIfNeeded(categoryCode);
        } finally {
          this.categoryModalLoading = false;
        }
      }
      this.ensureSlots();
    },

    closeCategoryModal() {
      this.categoryModalOpen = false;
    },

    onCategoryModalClosed() {
      this.modalCategoryCode = null;
    },

    /** Odd positions (1st, 3rd, …): white. Even positions: light grey. index is 0-based. */
    stripeClass(index) {
      return index % 2 === 0 ? "grafiche-stripe-odd" : "grafiche-stripe-even";
    },

    lang() {
      return this.$store.getters.language || "it";
    },

    categoryLabel(cat) {
      const loc = cat.title[this.lang()];
      return loc || cat.title.it || cat.title.en || cat.categoryCode;
    },

    lineTitle(item) {
      const t = item.title;
      if (!t || typeof t !== "object") return "";
      return t[this.lang()] || t.it || t.en || "";
    },

    itemsForCategory(categoryCode) {
      if (!this.vehicle.parts || !this.vehicle.parts[categoryCode]) return [];
      return this.vehicle.parts[categoryCode];
    },

    categoryAllSelected(categoryCode) {
      if (
        this.catalogFromPartCategoryGroupsApi &&
        this.lineTemplatesByCategory[categoryCode] === undefined
      ) {
        return false;
      }
      const items = this.itemsForCategory(categoryCode);
      if (!items.length) return false;
      return items.every((i) => i.selected);
    },

    async applyCategorySelectAll(categoryCode, value) {
      if (this.readonly) return;
      await this.prefetchCategoryPartsIfNeeded(categoryCode);
      this.ensureSlots();
      const items = this.itemsForCategory(categoryCode);
      items.forEach((i) => {
        i.selected = value;
      });
    },

    ensureSlots() {
      // Prevent re-entrant loops when reactive mutations inside ensureSlots
      // trigger the deep `vehicle` watcher synchronously.
      if (this.ensuringSlots) return;
      this.ensuringSlots = true;
      try {
      if (!this.vehicle.parts) this.vehicle.parts = {};
      for (const cat of this.categories) {
        const key = cat.categoryCode;
        const slot = this.vehicle.parts[key];
        if (Array.isArray(slot) && slot.length > 0) continue;

        const rawTemplates = this.lineTemplatesByCategory[key];
        if (
          this.catalogFromPartCategoryGroupsApi &&
          rawTemplates === undefined
        ) {
          continue;
        }

        const hasCatalogLines =
          rawTemplates !== undefined && rawTemplates !== null;

        const enabledRows = hasCatalogLines
          ? rawTemplates
              .filter((r) => r.enabled !== false)
              .map((r) => ({
                _id: r._id,
                partCode: r.partCode,
                title: r.title,
              }))
          : [];

        if (enabledRows.length > 0) {
          // Only assign when the slot is missing/empty to avoid infinite reactive loops.
          if (!Array.isArray(slot) || slot.length === 0) {
            this.vehicle.parts[key] =
              mergeLinesWithVehicleDefaults(enabledRows);
          }
          this.applyAllSelectedToSlot(key);
          continue;
        }

        if (hasCatalogLines) {
          if (!Array.isArray(slot) || slot.length !== 0) this.vehicle.parts[key] = [];
          continue;
        }

        if (typeof cat.buildLines === "function") {
          if (!Array.isArray(slot) || slot.length === 0) {
            this.vehicle.parts[key] = [...cat.buildLines(cat.categoryCode)];
          }
          this.applyAllSelectedToSlot(key);
          continue;
        }

        if (!Array.isArray(slot) || slot.length === 0) {
          this.vehicle.parts[key] = [
            ...buildMockTitlesForCategory(cat.categoryCode),
          ];
        }
        this.applyAllSelectedToSlot(key);
      }
      } finally {
        this.ensuringSlots = false;
      }
    },

    normalizeCategoryRow(row) {
      return {
        categoryCode: row.categoryCode || row.position,
        sortOrder: row.sortOrder ?? row.order ?? 0,
        title: row.title || { it: row.categoryCode || "", en: "", sq: "" },
        buildLines: row.buildLines,
      };
    },

    async loadCategories() {
      this.loadError = false;
      this.catalogFromPartCategoryGroupsApi = false;
      this.categoryPartsInflight = {};

      const fromEnv =
        typeof process !== "undefined" && process.env
          ? process.env.VUE_APP_GRAFICHE_CATALOG_URL
          : "";
      const url = (this.catalogUrl || fromEnv || "").trim();

      if (url) {
        try {
          const raw = await fetchGraficheCatalogFromUrl(url);
          this.categories = raw.map((c) => this.normalizeCategoryRow(c));
          this.lineTemplatesByCategory = {};
          return;
        } catch {
          this.loadError = true;
          this.categories = DEFAULT_GRAFICHE_CATEGORIES.map((c) =>
            this.normalizeCategoryRow(c),
          );
          this.lineTemplatesByCategory = {};
          return;
        }
      }

      try {
        const snap = await fetchPartCategoryGroupsCategoriesOnly();
        if (snap.categories?.length > 0) {
          this.categories = snap.categories.map((c) =>
            this.normalizeCategoryRow(c),
          );
          this.lineTemplatesByCategory = {};
          this.catalogFromPartCategoryGroupsApi = true;
          return;
        }
      } catch {
        /* fall through */
      }

      if (hasStoredGraficheCatalog()) {
        const snap = loadGraficheCatalog();
        this.categories = snap.categories.map((c) =>
          this.normalizeCategoryRow(c),
        );
        this.lineTemplatesByCategory = cloneLinesMap(snap.linesByCategory);
        return;
      }

      this.categories = DEFAULT_GRAFICHE_CATEGORIES.map((c) =>
        this.normalizeCategoryRow(c),
      );
      this.lineTemplatesByCategory = {};
    },
  },
};
</script>

<style scoped>
/* Zebra striping: odd rows white, even rows light grey (panels + inner lines) */
.infocar-grafiche
  :deep(.v-expansion-panel.grafiche-stripe-even .v-expansion-panel-title),
.infocar-grafiche
  :deep(.v-expansion-panel.grafiche-stripe-even .v-expansion-panel-text) {
  background-color: #f5f5f5;
}

.infocar-grafiche
  :deep(.v-expansion-panel.grafiche-stripe-odd .v-expansion-panel-title),
.infocar-grafiche
  :deep(.v-expansion-panel.grafiche-stripe-odd .v-expansion-panel-text) {
  background-color: #ffffff;
}

.grafiche-inner-row.grafiche-stripe-even {
  background-color: #f5f5f5;
}

.grafiche-inner-row.grafiche-stripe-odd {
  background-color: #ffffff;
}

/* Expansion text area matches panel stripe edge-to-edge */
.infocar-grafiche :deep(.v-expansion-panel-text.grafiche-panel-body) {
  padding-inline: 0;
}

.grafiche-quick-categories {
  border-color: rgba(0, 0, 0, 0.12);
}

.grafiche-quick-row.grafiche-stripe-even {
  background-color: #f5f5f5;
}

.grafiche-quick-row.grafiche-stripe-odd {
  background-color: #ffffff;
}

.infocar-grafiche .grafiche-quick-row {
  cursor: pointer;
}

.infocar-grafiche .grafiche-quick-category-check {
  cursor: pointer;
}

.infocar-grafiche .grafiche-quick-category-check :deep(.v-selection-control) {
  align-items: center;
}
</style>
